import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Logo } from "./Logo";
import { Moon, Sun, LogOut, LayoutDashboard, Shield, LogIn } from "lucide-react";
import { useEffect, useState } from "react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function Nav() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [dark, setDark] = useState(() =>
        typeof document !== "undefined" && document.documentElement.classList.contains("dark")
    );

    useEffect(() => {
        const stored = localStorage.getItem("splink-theme");
        if (stored === "dark") {
            document.documentElement.classList.add("dark");
            setDark(true);
        }
    }, []);

    const toggleTheme = () => {
        const next = !dark;
        setDark(next);
        document.documentElement.classList.toggle("dark", next);
        localStorage.setItem("splink-theme", next ? "dark" : "light");
    };

    return (
        <header className="sticky top-0 z-40 w-full border-b border-border bg-background/85 backdrop-blur-xl" data-testid="site-nav">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
                <Logo />
                <nav className="flex items-center gap-2">
                    <Link
                        to="/about"
                        className="hidden text-sm font-medium text-muted-foreground hover:text-foreground sm:inline-block sm:px-3"
                        data-testid="nav-about-link"
                    >
                        About
                    </Link>
                    <Link
                        to="/terms"
                        className="hidden text-sm font-medium text-muted-foreground hover:text-foreground sm:inline-block sm:px-3"
                        data-testid="nav-terms-link"
                    >
                        Terms
                    </Link>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleTheme}
                        aria-label="Toggle theme"
                        data-testid="theme-toggle-btn"
                    >
                        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </Button>
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="user-menu-btn">
                                    {user.name || user.email}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="font-mono text-xs">
                                    {user.email}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                                    <LayoutDashboard className="mr-2 h-4 w-4" />
                                    Dashboard
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                                        <Shield className="mr-2 h-4 w-4" />
                                        Admin Console
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={async () => { await logout(); navigate("/"); }}
                                    data-testid="menu-logout"
                                >
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate("/login")}
                                data-testid="nav-login-btn"
                            >
                                <LogIn className="mr-2 h-4 w-4" />
                                Sign in
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => navigate("/register")}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="nav-register-btn"
                            >
                                Get started
                            </Button>
                        </>
                    )}
                </nav>
            </div>
        </header>
    );
}
